import React, { useState, useEffect, useCallback } from 'react';
import api, { businessDaysAPI, posSessionsAPI, formatMoney } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { 
  Calendar, Sun, Moon, Clock, Lock, Unlock, CheckCircle2, 
  AlertTriangle, TrendingUp, Banknote, CreditCard, ArrowRight,
  History, FileText, X, Shield, Printer
} from 'lucide-react';
import { PinPad } from '@/components/PinPad';
import { NeoDatePicker } from '@/components/DateTimePicker';
import { notify } from '@/lib/notify';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import ReportXZ from '@/components/ReportXZ';

/**
 * BusinessDayManager - Componente para gestionar Jornadas de Trabajo
 * 
 * Flujo:
 * 1. Verifica si hay jornada abierta
 * 2. Permite abrir/cerrar jornada con autorización de gerente
 * 3. Muestra estadísticas de la jornada actual
 */
export default function BusinessDayManager({ 
  compact = false,
  onDayStatusChange,
  showStatsInline = false 
}) {
  const { user, logout, hasPermission } = useAuth();
  const { isMinimalist, isNeoDark } = useTheme();
  
  // Determine if we're on a light background (minimalist light mode)
  const isLightBg = isMinimalist && !isNeoDark;
  
  const [businessDay, setBusinessDay] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  
  // Dialogs
  const [openDayDialog, setOpenDayDialog] = useState(false);
  const [closeDayDialog, setCloseDayDialog] = useState(false);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [pinDialog, setPinDialog] = useState({ open: false, action: null });
  const [reportZDialog, setReportZDialog] = useState({ open: false, dayId: null });
  // 🔒 CIERRE-DIA-AUTO-PRINT-2026-05-22 — flag para que tras imprimir/cancelar
  // el modal post-cierre, se ejecute logout (cerrando la jornada de verdad).
  const [closeDayLogoutPending, setCloseDayLogoutPending] = useState(false);
  
  // Form states
  const [authorizerPin, setAuthorizerPin] = useState('');
  const [authorizer, setAuthorizer] = useState(null);
  const [businessDate, setBusinessDate] = useState('');
  const [openingNotes, setOpeningNotes] = useState('');
  const [closingNotes, setClosingNotes] = useState('');
  const [forceClose, setForceClose] = useState(false);
  const [processing, setProcessing] = useState(false);
  // 🔒 PLAN-B-CIERRE-FORZADO-2026-05-08
  const [closePreview, setClosePreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Cargar estado actual
  const fetchCurrentDay = useCallback(async () => {
    try {
      setLoading(true);
      const res = await businessDaysAPI.current();
      setBusinessDay(res.data.business_day);
      setStats(res.data.stats);
      
      if (onDayStatusChange) {
        onDayStatusChange(res.data.has_open_day, res.data.business_day);
      }
    } catch (err) {
      console.error('Error fetching business day:', err);
    } finally {
      setLoading(false);
    }
  }, [onDayStatusChange]);

  useEffect(() => {
    fetchCurrentDay();
  }, [fetchCurrentDay]);

  // Cargar historial
  const fetchHistory = async () => {
    try {
      const res = await businessDaysAPI.history({ limit: 30 });
      setHistory(res.data || []);
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  };

  // Verificar PIN de autorización
  const handleVerifyPin = async () => {
    if (!authorizerPin || authorizerPin.length < 4) {
      notify.error('Ingresa un PIN válido');
      return;
    }
    
    setProcessing(true);
    try {
      const res = await businessDaysAPI.authorize(authorizerPin, pinDialog.action);
      setAuthorizer(res.data);
      setPinDialog({ open: false, action: null });
      
      // Abrir el diálogo correspondiente
      if (pinDialog.action === 'open') {
        setBusinessDate(new Date().toISOString().split('T')[0]);
        setOpenDayDialog(true);
      } else if (pinDialog.action === 'close') {
        // 🔒 PLAN-B-CIERRE-FORZADO-2026-05-08 — cargar preview ANTES de mostrar
        // el modal para detectar bills/órdenes/mesas pendientes y advertir
        // al usuario, sin bloquearle el cierre.
        setLoadingPreview(true);
        try {
          const prev = await businessDaysAPI.closePreview();
          setClosePreview(prev.data);
        } catch (e) {
          // Si falla preview, NO bloqueamos — el usuario podrá intentar cerrar
          // y el backend mostrará el error real. Logueamos para depuración.
          console.warn('[close-preview] failed:', e?.response?.data?.detail || e?.message);
          setClosePreview(null);
        } finally {
          setLoadingPreview(false);
        }
        setCloseDayDialog(true);
      }
    } catch (err) {
      notify.error(err.response?.data?.detail || 'PIN inválido o sin autorización');
    } finally {
      setProcessing(false);
    }
  };

  // Abrir Jornada
  const handleOpenDay = async () => {
    if (!authorizer) {
      notify.error('Se requiere autorización');
      return;
    }
    
    setProcessing(true);
    try {
      const res = await businessDaysAPI.open({
        business_date: businessDate,
        authorizer_pin: authorizerPin,
        opening_notes: openingNotes || null
      });
      
      notify.success(res.data.message);
      setOpenDayDialog(false);
      setAuthorizer(null);
      setAuthorizerPin('');
      setOpeningNotes('');
      fetchCurrentDay();
    } catch (err) {
      notify.error(err.response?.data?.detail || 'Error al abrir jornada');
    } finally {
      setProcessing(false);
    }
  };

  // Cerrar Jornada
  const handleCloseDay = async () => {
    if (!authorizer) {
      notify.error('Se requiere autorización');
      return;
    }
    
    setProcessing(true);
    try {
      // 🔒 PLAN-B-CIERRE-FORZADO-2026-05-08 — Si el preview detectó pendientes,
      // enviar force_close_with_orphans=true para que el backend cancele
      // bills/órdenes/mesas en cascada antes de la validación bloqueante.
      const hasOrphans = !!(closePreview && closePreview.needs_force_close);
      const res = await businessDaysAPI.close({
        authorizer_pin: authorizerPin,
        closing_notes: closingNotes || null,
        force_close: forceClose,
        force_close_with_orphans: hasOrphans
      });
      
      // Mensaje enriquecido si hubo limpieza forzada
      const fc = res.data?.forced_cleanup;
      if (fc) {
        const parts = [];
        if (fc.bills_cancelled > 0) parts.push(`${fc.bills_cancelled} cuenta(s) cancelada(s)`);
        if (fc.orders_cancelled > 0) parts.push(`${fc.orders_cancelled} orden(es) cancelada(s)`);
        if (fc.tables_freed > 0) parts.push(`${fc.tables_freed} mesa(s) liberada(s)`);
        notify.success(res.data.message, {
          description: parts.length ? `Limpieza automática: ${parts.join(' · ')}` : undefined,
          duration: 6000
        });
      } else {
        notify.success(res.data.message);
      }
      setCloseDayDialog(false);
      setAuthorizer(null);
      setAuthorizerPin('');
      setClosingNotes('');
      setForceClose(false);
      setClosePreview(null);

      // 🔒 CIERRE-DIA-AUTO-PRINT-2026-05-22 — Antes hacíamos `setTimeout(logout)`
      // directo, lo cual cerraba el día sin imprimir el Reporte Z. Ahora abrimos
      // el modal de impresión (mismo flujo que el botón "Reporte Z" antes
      // unificado) y marcamos un flag para que el logout ocurra cuando el
      // modal se cierre (sea por imprimir o por cancelar). NON-BLOCKING:
      // si la impresión falla, la jornada YA está cerrada en DB y el modal
      // igual permite logout.
      const closedDayId = businessDay?.id;
      if (closedDayId) {
        setCloseDayLogoutPending(true);
        setReportZDialog({ open: true, dayId: closedDayId });
      } else {
        // Fallback defensivo: si por algún motivo no tenemos businessDay.id,
        // hacemos logout sin imprimir (la jornada YA quedó cerrada).
        setTimeout(() => {
          logout();
          window.location.href = '/login';
        }, 2000);
      }
    } catch (err) {
      notify.error(err.response?.data?.detail || 'Error al cerrar jornada');
    } finally {
      setProcessing(false);
    }
  };

  // Iniciar proceso de apertura/cierre
  const startAction = async (action) => {
    // If user already has close_day permission, skip PIN authorization
    if (hasPermission('close_day')) {
      setAuthorizer({ name: user?.name, id: user?.id });
      setAuthorizerPin('self');
      if (action === 'open') {
        setBusinessDate(new Date().toISOString().split('T')[0]);
        setOpenDayDialog(true);
      } else if (action === 'close') {
        // 🔒 CLOSE-OTHER-SHIFTS-2026-05-27 — Cargar closePreview también cuando el admin
        // omite el PIN dialog, para que la sección "Cuentas Pendientes" + turnos aparezca.
        setLoadingPreview(true);
        try {
          const prev = await businessDaysAPI.closePreview();
          setClosePreview(prev.data);
        } catch (e) {
          console.warn('[close-preview] failed:', e?.response?.data?.detail || e?.message);
          setClosePreview(null);
        } finally {
          setLoadingPreview(false);
        }
        setCloseDayDialog(true);
      }
      return;
    }
    setAuthorizerPin('');
    setAuthorizer(null);
    setPinDialog({ open: true, action });
  };

  // Formato de fecha legible
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('es-DO', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    return new Date(isoStr).toLocaleTimeString('es-DO', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true
    });
  };

  // Vista compacta (para sidebar o header)
  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {loading ? (
          <div className="animate-pulse flex items-center gap-2">
            <div className="w-4 h-4 bg-white/20 rounded-full" />
            <div className="w-20 h-4 bg-white/20 rounded" />
          </div>
        ) : businessDay ? (
          <div 
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/20 border border-green-500/30 cursor-pointer hover:bg-green-500/30 transition-all"
            onClick={() => {
              fetchHistory();
              setHistoryDialog(true);
            }}
            data-testid="business-day-indicator"
          >
            <Sun size={14} className="text-green-500" />
            <span className="text-green-700 dark:text-green-300 text-xs font-medium">
              {businessDay.business_date}
            </span>
          </div>
        ) : (
          <button
            onClick={() => startAction('open')}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/20 border border-red-500/30 hover:bg-red-500/30 transition-all animate-pulse"
            data-testid="open-day-compact-btn"
          >
            <Moon size={14} className="text-red-500" />
            <span className="text-red-700 dark:text-red-300 text-xs font-medium">
              Sin Jornada
            </span>
          </button>
        )}
      </div>
    );
  }

  // Vista completa
  // Theme-adaptive colors
  const colors = isLightBg ? {
    // Light theme colors (dark text on light bg)
    titleOpen: '#166534',      // Green 800
    dateOpen: '#374151',       // Gray 700
    titleClosed: '#DC2626',    // Red 600
    dateClosed: '#374151',     // Gray 700
    infoLabel: '#6B7280',      // Gray 500
    infoValue: '#1F2937',      // Gray 800
    refValue: '#0891B2',       // Cyan 600
    statLabel: '#6B7280',      // Gray 500
    statVentas: '#166534',     // Green 800
    statEfectivo: '#065F46',   // Emerald 800
    statTarjeta: '#1E40AF',    // Blue 800
    statFacturas: '#0E7490',   // Cyan 700
    historyBtn: '#1F2937',     // Gray 800 (darker for better visibility)
    historyBorder: 'border-gray-500 hover:bg-gray-100 bg-gray-50',
    infoBorder: 'border-gray-200',
    statBgVentas: 'bg-green-50 border border-green-200 shadow-sm',
    statBgEfectivo: 'bg-emerald-50 border border-emerald-200 shadow-sm',
    statBgTarjeta: 'bg-blue-50 border border-blue-200 shadow-sm',
    statBgFacturas: 'bg-cyan-50 border border-cyan-200 shadow-sm',
  } : {
    // Dark theme colors (light text on dark bg)
    titleOpen: '#4ADE80',      // Green 400
    dateOpen: '#86EFAC',       // Green 300
    titleClosed: '#F87171',    // Red 400
    dateClosed: '#FCA5A5',     // Red 300
    infoLabel: '#9CA3AF',      // Gray 400
    infoValue: '#F3F4F6',      // Gray 100
    refValue: '#22D3EE',       // Cyan 400
    statLabel: '#9CA3AF',      // Gray 400
    statVentas: '#4ADE80',     // Green 400
    statEfectivo: '#34D399',   // Emerald 400
    statTarjeta: '#60A5FA',    // Blue 400
    statFacturas: '#22D3EE',   // Cyan 400
    historyBtn: '#D1D5DB',     // Gray 300
    historyBorder: 'border-white/30 hover:bg-white/10',
    infoBorder: 'border-white/10',
    statBgVentas: 'bg-white/5',
    statBgEfectivo: 'bg-white/5',
    statBgTarjeta: 'bg-white/5',
    statBgFacturas: 'bg-white/5',
  };

  return (
    <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1" style={{ WebkitOverflowScrolling: 'touch' }}>
      {/* Estado actual */}
      <div 
        className={`rounded-2xl p-4 sm:p-5 transition-all ${
          businessDay 
            ? 'bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20' 
            : 'bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/20'
        }`}
      >
        {/* Header: Title + Date */}
        <div className="flex items-start gap-3 mb-4">
          {businessDay ? (
            <>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <Sun size={20} style={{ color: colors.titleOpen }} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 
                  className="font-oswald font-bold text-base sm:text-lg"
                  style={{ 
                    color: colors.titleOpen,
                    WebkitTextFillColor: colors.titleOpen,
                    opacity: 1 
                  }}
                >
                  JORNADA ABIERTA
                </h3>
                <p 
                  className="text-sm"
                  style={{ 
                    color: colors.dateOpen,
                    WebkitTextFillColor: colors.dateOpen,
                    opacity: 1 
                  }}
                >
                  {formatDate(businessDay.business_date)}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-red-500/20 flex items-center justify-center animate-pulse flex-shrink-0">
                <Moon size={20} style={{ color: colors.titleClosed }} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 
                  className="font-oswald font-bold text-base sm:text-lg"
                  style={{ 
                    color: colors.titleClosed,
                    WebkitTextFillColor: colors.titleClosed,
                    opacity: 1 
                  }}
                >
                  SIN JORNADA
                </h3>
                <p 
                  className="text-sm"
                  style={{ 
                    color: colors.dateClosed,
                    WebkitTextFillColor: colors.dateClosed,
                    opacity: 1 
                  }}
                >
                  Debe abrir el día para operar
                </p>
              </div>
            </>
          )}
        </div>
        
        {/* Action Buttons - Stack on mobile */}
        <div className="flex flex-col sm:flex-row gap-2">
          {businessDay ? (
            <>
              {/* 🔒 CIERRE-DIA-AUTO-PRINT-2026-05-22 — Botón "Reporte Z" eliminado
                  por confusión de usuarios. El botón "Cerrar Día" ahora abre el
                  modal de impresión automáticamente tras cerrar la jornada. La
                  reimpresión desde historial sigue disponible en el botón
                  "Historial" (icono de calendario). */}
              <Button
                onClick={() => startAction('close')}
                className="flex-1 min-h-[48px] bg-red-600 hover:bg-red-700 border-0"
                style={{ 
                  color: '#FFFFFF',
                  WebkitTextFillColor: '#FFFFFF',
                  WebkitAppearance: 'none',
                  opacity: 1
                }}
                data-testid="close-day-btn"
              >
                <Lock size={16} className="mr-2 flex-shrink-0" style={{ color: '#FFFFFF' }} />
                <span>Cerrar Día</span>
              </Button>
              <Button
                onClick={() => {
                  fetchHistory();
                  setHistoryDialog(true);
                }}
                variant="outline"
                className={`min-h-[48px] min-w-[48px] sm:w-auto`}
                style={{ 
                  color: colors.historyBtn,
                  WebkitTextFillColor: colors.historyBtn,
                  WebkitAppearance: 'none',
                  opacity: 1,
                  borderWidth: '2px',
                  borderColor: isLightBg ? '#6B7280' : 'rgba(255,255,255,0.3)',
                  backgroundColor: isLightBg ? '#F3F4F6' : 'transparent'
                }}
                data-testid="history-btn"
              >
                <History size={18} style={{ color: colors.historyBtn, stroke: colors.historyBtn, strokeWidth: 2.5, minWidth: '18px', minHeight: '18px' }} />
                <span className="sm:hidden ml-2">Historial</span>
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => startAction('open')}
                className="flex-1 min-h-[48px] bg-green-600 hover:bg-green-700 border-0"
                style={{ 
                  color: '#FFFFFF',
                  WebkitTextFillColor: '#FFFFFF',
                  WebkitAppearance: 'none',
                  opacity: 1
                }}
                data-testid="open-day-btn"
              >
                <Unlock size={16} className="mr-2 flex-shrink-0" style={{ color: '#FFFFFF' }} />
                <span>Abrir Día</span>
              </Button>
              <Button
                onClick={() => {
                  fetchHistory();
                  setHistoryDialog(true);
                }}
                variant="outline"
                className={`min-h-[48px] min-w-[48px] sm:w-auto`}
                style={{ 
                  color: colors.historyBtn,
                  WebkitTextFillColor: colors.historyBtn,
                  WebkitAppearance: 'none',
                  opacity: 1,
                  borderWidth: '2px',
                  borderColor: isLightBg ? '#6B7280' : 'rgba(255,255,255,0.3)',
                  backgroundColor: isLightBg ? '#F3F4F6' : 'transparent'
                }}
                data-testid="history-btn-closed"
              >
                <History size={18} style={{ color: colors.historyBtn, stroke: colors.historyBtn, strokeWidth: 2.5, minWidth: '18px', minHeight: '18px' }} />
                <span className="sm:hidden ml-2">Historial</span>
              </Button>
            </>
          )}
        </div>
        
        {/* Info de la jornada */}
        {businessDay && (
          <div className={`grid grid-cols-2 gap-3 mt-4 pt-4 border-t ${colors.infoBorder}`}>
            <div className="text-center">
              <p className="text-xs mb-1" style={{ color: colors.infoLabel, WebkitTextFillColor: colors.infoLabel, opacity: 1 }}>Abierta</p>
              <p className="font-medium text-sm" style={{ color: colors.infoValue, WebkitTextFillColor: colors.infoValue, opacity: 1 }}>{formatTime(businessDay.opened_at)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs mb-1" style={{ color: colors.infoLabel, WebkitTextFillColor: colors.infoLabel, opacity: 1 }}>Autorizado</p>
              <p className="font-medium text-sm" style={{ color: colors.infoValue, WebkitTextFillColor: colors.infoValue, opacity: 1 }}>{businessDay.authorized_by_name || 'Admin'}</p>
            </div>
            <div className="text-center">
              <p className="text-xs mb-1" style={{ color: colors.infoLabel, WebkitTextFillColor: colors.infoLabel, opacity: 1 }}>Por</p>
              <p className="font-medium text-sm" style={{ color: colors.infoValue, WebkitTextFillColor: colors.infoValue, opacity: 1 }}>{businessDay.opened_by_name}</p>
            </div>
            <div className="text-center">
              <p className="text-xs mb-1" style={{ color: colors.infoLabel, WebkitTextFillColor: colors.infoLabel, opacity: 1 }}>Referencia</p>
              <p className="font-mono text-sm" style={{ color: colors.refValue, WebkitTextFillColor: colors.refValue, opacity: 1 }}>{businessDay.ref}</p>
            </div>
          </div>
        )}
        
        {/* Estadísticas del día */}
        {businessDay && stats && showStatsInline && (
          <div className={`grid grid-cols-2 gap-2 mt-4 pt-4 border-t ${colors.infoBorder}`}>
            <div className={`${colors.statBgVentas} rounded-xl p-3 text-center`}>
              <TrendingUp size={18} style={{ color: colors.statVentas, stroke: colors.statVentas, strokeWidth: 2 }} className="mx-auto mb-1" />
              <p className="text-xs mb-0.5" style={{ color: colors.statLabel, WebkitTextFillColor: colors.statLabel, opacity: 1 }}>Ventas</p>
              <p className="font-oswald font-bold text-sm" style={{ color: colors.statVentas, WebkitTextFillColor: colors.statVentas, opacity: 1 }}>{formatMoney(stats.total_sales)}</p>
            </div>
            <div className={`${colors.statBgEfectivo} rounded-xl p-3 text-center`}>
              <Banknote size={18} style={{ color: colors.statEfectivo, stroke: colors.statEfectivo, strokeWidth: 2 }} className="mx-auto mb-1" />
              <p className="text-xs mb-0.5" style={{ color: colors.statLabel, WebkitTextFillColor: colors.statLabel, opacity: 1 }}>Efectivo</p>
              <p className="font-oswald font-bold text-sm" style={{ color: colors.statEfectivo, WebkitTextFillColor: colors.statEfectivo, opacity: 1 }}>{formatMoney(stats.total_cash)}</p>
            </div>
            <div className={`${colors.statBgTarjeta} rounded-xl p-3 text-center`}>
              <CreditCard size={18} style={{ color: colors.statTarjeta, stroke: colors.statTarjeta, strokeWidth: 2 }} className="mx-auto mb-1" />
              <p className="text-xs mb-0.5" style={{ color: colors.statLabel, WebkitTextFillColor: colors.statLabel, opacity: 1 }}>Tarjeta</p>
              <p className="font-oswald font-bold text-sm" style={{ color: colors.statTarjeta, WebkitTextFillColor: colors.statTarjeta, opacity: 1 }}>{formatMoney(stats.total_card)}</p>
            </div>
            <div className={`${colors.statBgFacturas} rounded-xl p-3 text-center`}>
              <FileText size={18} style={{ color: colors.statFacturas, stroke: colors.statFacturas, strokeWidth: 2 }} className="mx-auto mb-1" />
              <p className="text-xs mb-0.5" style={{ color: colors.statLabel, WebkitTextFillColor: colors.statLabel, opacity: 1 }}>Facturas</p>
              <p className="font-oswald font-bold text-sm" style={{ color: colors.statFacturas, WebkitTextFillColor: colors.statFacturas, opacity: 1 }}>{stats.total_invoices}</p>
            </div>
          </div>
        )}
      </div>

      {/* Dialogo PIN de Autorización */}
      <Dialog open={pinDialog.open} onOpenChange={(open) => !open && setPinDialog({ open: false, action: null })}>
        <DialogContent className="bg-slate-900/95 backdrop-blur-xl border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-oswald text-white flex items-center gap-2">
              <Shield className="text-amber-400" size={20} />
              Autorización Requerida
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Ingresa el PIN de un Gerente o Administrador para {pinDialog.action === 'open' ? 'abrir' : 'cerrar'} la jornada
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <PinPad
              value={authorizerPin}
              onChange={setAuthorizerPin}
              label="PIN de Autorizacion"
              placeholder="Ingresa PIN"
              forceKeypad
            />
            
            <div className="flex gap-3">
              <Button
                onClick={() => setPinDialog({ open: false, action: null })}
                variant="outline"
                className="flex-1 border-white/20 text-white/70"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleVerifyPin}
                disabled={processing || authorizerPin.length < 4}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="verify-pin-btn"
              >
                {processing ? 'Verificando...' : 'Verificar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialogo Abrir Día */}
      <Dialog open={openDayDialog} onOpenChange={setOpenDayDialog}>
        <DialogContent className="bg-slate-900/95 backdrop-blur-xl border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-oswald text-white flex items-center gap-2">
              <Sun className="text-green-400" size={20} />
              Abrir Jornada de Trabajo
            </DialogTitle>
            {authorizer && (
              <Badge className="bg-green-500/20 text-green-300 w-fit">
                Autorizado por: {authorizer.authorizer_name}
              </Badge>
            )}
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div>
              <label className="text-white/70 text-sm block mb-2">Fecha de Negocio (Contable)</label>
              <NeoDatePicker
                value={businessDate}
                onChange={(e) => setBusinessDate(e.target.value)}
                className="w-full h-12 bg-background border border-border rounded-xl px-4 text-sm font-mono"
              />
              <p className="text-white/40 text-xs mt-1">
                Esta fecha se usará para todas las transacciones hasta el cierre
              </p>
            </div>
            
            <div>
              <label className="text-white/70 text-sm block mb-2">Notas de Apertura (Opcional)</label>
              <textarea
                value={openingNotes}
                onChange={(e) => setOpeningNotes(e.target.value)}
                placeholder="Ej: Inicio normal, fondo de caja verificado..."
                className="w-full h-20 bg-white/5 border border-white/20 rounded-xl px-4 py-2 text-white placeholder-white/30 focus:border-green-400 outline-none resize-none"
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setOpenDayDialog(false)}
                variant="outline"
                className="flex-1 border-white/20 text-white/70"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleOpenDay}
                disabled={processing || !businessDate}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                data-testid="confirm-open-day-btn"
              >
                {processing ? 'Abriendo...' : 'Abrir Jornada'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialogo Cerrar Día */}
      <Dialog open={closeDayDialog} onOpenChange={setCloseDayDialog}>
        <DialogContent className="bg-slate-900/95 backdrop-blur-xl border-white/10 max-w-md max-h-[85vh] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          <DialogHeader>
            <DialogTitle className="font-oswald text-white flex items-center gap-2">
              <Moon className="text-red-400" size={20} />
              Cerrar Jornada de Trabajo
            </DialogTitle>
            {authorizer && (
              <Badge className="bg-red-500/20 text-red-300 w-fit">
                Autorizado por: {authorizer.authorizer_name}
              </Badge>
            )}
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {/* 🔒 PLAN-B-CIERRE-FORZADO-2026-05-08 — Aviso de cuentas pendientes
                cuando preview detecta orphans. Listado scrolleable, theme-aware,
                touch-friendly (mínimo 48x48 en cada item). Cero bloqueo: el
                botón sigue habilitado, solo cambia su rotulado. */}
            {loadingPreview && (
              <div
                className="rounded-xl px-4 py-3 text-sm flex items-center gap-2"
                style={{
                  background: 'var(--muted, rgba(255,255,255,0.05))',
                  color: 'var(--muted-foreground, rgba(255,255,255,0.6))',
                  border: '1px solid var(--border, rgba(255,255,255,0.1))',
                }}
                data-testid="close-preview-loading"
              >
                <Clock size={14} className="animate-spin" />
                Verificando cuentas pendientes…
              </div>
            )}
            {closePreview?.needs_force_close && (
              <div
                className="rounded-xl p-4 space-y-3"
                style={{
                  background: 'rgba(245, 158, 11, 0.10)',
                  border: '1px solid rgba(245, 158, 11, 0.35)',
                }}
                data-testid="close-day-warning"
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle
                    size={20}
                    className="flex-shrink-0 mt-0.5"
                    style={{ color: '#F59E0B' }}
                  />
                  <div className="min-w-0 flex-1">
                    <p
                      className="font-oswald font-bold text-sm sm:text-base"
                      style={{ color: '#FCD34D' }}
                    >
                      Cuentas Pendientes Detectadas
                    </p>
                    <p className="text-xs sm:text-sm mt-1" style={{ color: 'rgba(252,211,77,0.85)' }}>
                      Al cerrar la jornada, estas serán <b>canceladas automáticamente</b>:
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div
                    className="rounded-lg py-2 px-1"
                    style={{ background: 'rgba(0,0,0,0.25)' }}
                    data-testid="warning-bills-count"
                  >
                    <p className="font-oswald font-bold text-lg sm:text-xl" style={{ color: '#FCD34D' }}>
                      {closePreview.pending_bills?.length || 0}
                    </p>
                    <p className="text-[10px] sm:text-xs uppercase tracking-wide" style={{ color: 'rgba(252,211,77,0.7)' }}>
                      Cuentas
                    </p>
                  </div>
                  <div
                    className="rounded-lg py-2 px-1"
                    style={{ background: 'rgba(0,0,0,0.25)' }}
                    data-testid="warning-orders-count"
                  >
                    <p className="font-oswald font-bold text-lg sm:text-xl" style={{ color: '#FCD34D' }}>
                      {closePreview.pending_orders?.length || 0}
                    </p>
                    <p className="text-[10px] sm:text-xs uppercase tracking-wide" style={{ color: 'rgba(252,211,77,0.7)' }}>
                      Órdenes
                    </p>
                  </div>
                  <div
                    className="rounded-lg py-2 px-1"
                    style={{ background: 'rgba(0,0,0,0.25)' }}
                    data-testid="warning-tables-count"
                  >
                    <p className="font-oswald font-bold text-lg sm:text-xl" style={{ color: '#FCD34D' }}>
                      {closePreview.occupied_tables?.length || 0}
                    </p>
                    <p className="text-[10px] sm:text-xs uppercase tracking-wide" style={{ color: 'rgba(252,211,77,0.7)' }}>
                      Mesas
                    </p>
                  </div>
                </div>
                {(closePreview.pending_bills?.length > 0
                  || closePreview.pending_orders?.length > 0) && (
                  <div
                    className="max-h-40 overflow-y-auto rounded-lg p-2 space-y-1 text-xs"
                    style={{ background: 'rgba(0,0,0,0.25)', WebkitOverflowScrolling: 'touch' }}
                    data-testid="warning-details-list"
                  >
                    {(closePreview.pending_bills || []).map((b) => (
                      <div
                        key={`pb-${b.id}`}
                        className="flex justify-between items-center min-h-[44px] px-2 py-1.5"
                        style={{ color: 'rgba(252,211,77,0.95)' }}
                      >
                        <span className="truncate">
                          🧾 {b.label || `Mesa ${b.table_number || '?'}`}
                          {b.transaction_number ? ` (T-${b.transaction_number})` : ''}
                        </span>
                        <span className="font-oswald font-bold ml-2 flex-shrink-0">
                          {formatMoney(b.total)}
                        </span>
                      </div>
                    ))}
                    {(closePreview.pending_orders || []).map((o) => (
                      <div
                        key={`po-${o.id}`}
                        className="flex justify-between items-center min-h-[44px] px-2 py-1.5"
                        style={{ color: 'rgba(252,211,77,0.95)' }}
                      >
                        <span className="truncate">
                          📝 Mesa {o.table_number || '?'} · {o.waiter_name}
                        </span>
                        <span className="text-[10px] uppercase ml-2 flex-shrink-0 opacity-70">
                          {o.active_items_count} items
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {/* 🔒 CLOSE-OTHER-SHIFTS-2026-05-27 — Feature 2: Lista interactiva de turnos abiertos */}
                {(closePreview.pending_sessions?.length > 0) && (
                  <div
                    className="rounded-lg p-2 space-y-2 text-xs mt-2"
                    style={{ background: 'rgba(0,0,0,0.25)' }}
                    data-testid="pending-sessions-list"
                  >
                    <p className="font-bold text-xs uppercase tracking-wide mb-1" style={{ color: '#FCD34D' }}>
                      Turnos Abiertos ({closePreview.pending_sessions.length})
                    </p>
                    {closePreview.pending_sessions.map((s) => (
                      <div
                        key={`ps-${s.id}`}
                        className="flex justify-between items-center min-h-[44px] px-2 py-1.5 rounded"
                        style={{ background: 'rgba(245, 158, 11, 0.10)' }}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate" style={{ color: 'rgba(252,211,77,0.95)' }}>
                            👤 {s.opened_by_name || 'Usuario'}
                          </p>
                          <p className="text-[10px] truncate" style={{ color: 'rgba(252,211,77,0.7)' }}>
                            {s.terminal_name || 'Sin terminal'} {s.ref ? `• ${s.ref}` : ''}
                          </p>
                        </div>
                        {hasPermission('close_other_shifts') && (
                          <button
                            onClick={async () => {
                              try {
                                setProcessing(true);
                                await posSessionsAPI.close(s.id, {
                                  cash_declared: 0,
                                  notes: "Cerrado remotamente por " + (user?.name || "Admin")
                                });
                                // 🔒 SHIFT-PRINT-FIX-2026-05-28 — Auto-impresión del cierre remoto
                                try {
                                  const rep = await api.get(`/business-days/session/${s.id}/report-x`);
                                  if (rep?.data) {
                                    await api.post('/print/report-shift', {
                                      report: rep.data,
                                      detailed: true,
                                      type: 'X',
                                    });
                                  }
                                } catch (printErr) {
                                  console.warn('Auto-print cierre remoto falló:', printErr);
                                }
                                notify.success('Turno cerrado');
                                const newPreview = await businessDaysAPI.closePreview();
                                setClosePreview(newPreview.data);
                              } catch (err) {
                                notify.error('Error cerrando turno', { 
                                  description: typeof err.response?.data?.detail === 'string'
                                    ? err.response.data.detail
                                    : JSON.stringify(err.response?.data?.detail || err.message)
                                });
                              } finally {
                                setProcessing(false);
                              }
                            }}
                            disabled={processing}
                            className="ml-2 px-3 py-1 text-xs bg-amber-500 hover:bg-amber-400 text-white rounded font-bold disabled:opacity-50 shrink-0"
                            data-testid={`close-session-${s.id}`}
                          >
                            {processing ? '...' : 'Cerrar'}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Resumen del día */}
            {stats && (
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <h4 className="text-white/70 text-sm mb-3">Resumen del Día</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-white/50">Ventas Totales:</span>
                    <p className="font-oswald font-bold text-green-400">{formatMoney(stats.total_sales)}</p>
                  </div>
                  <div>
                    <span className="text-white/50">Facturas:</span>
                    <p className="font-oswald font-bold text-cyan-400">{stats.total_invoices}</p>
                  </div>
                  <div>
                    <span className="text-white/50">Efectivo:</span>
                    <p className="font-oswald font-bold text-emerald-400">{formatMoney(stats.total_cash)}</p>
                  </div>
                  <div>
                    <span className="text-white/50">Tarjeta:</span>
                    <p className="font-oswald font-bold text-blue-400">{formatMoney(stats.total_card)}</p>
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <label className="text-white/70 text-sm block mb-2">Notas de Cierre (Opcional)</label>
              <textarea
                value={closingNotes}
                onChange={(e) => setClosingNotes(e.target.value)}
                placeholder="Ej: Cierre sin novedad, efectivo cuadrado..."
                className="w-full h-20 bg-white/5 border border-white/20 rounded-xl px-4 py-2 text-white placeholder-white/30 focus:border-red-400 outline-none resize-none"
              />
            </div>
            
            {/* 🔒 FORCE-CLOSE-ADMIN-2026-06-03 — Solo Super Admin puede forzar cierre */}
            {user?.is_super_admin && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={forceClose}
                  onChange={(e) => setForceClose(e.target.checked)}
                  className="w-4 h-4 rounded border-white/30 bg-white/5 text-amber-500 focus:ring-amber-500"
                />
                <span className="text-white/70 text-sm">Forzar cierre (cierra turnos abiertos)</span>
              </label>
            )}
            
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => { setCloseDayDialog(false); setClosePreview(null); }}
                variant="outline"
                className="flex-1 border-white/20 text-white/70"
                style={{ minHeight: '48px' }}
              >
                Cancelar
              </Button>
              {/* 🔒 CLOSE-OTHER-SHIFTS-2026-05-27 — Botón deshabilitado si hay turnos abiertos.
                  Rótulo: "Confirmar Cierre" por defecto, "Confirmar Cierre Forzado" solo si checkbox marcado. */}
              <Button
                onClick={handleCloseDay}
                disabled={processing || loadingPreview || (closePreview?.needs_force_close && !forceClose)}
                className={`flex-1 text-white ${forceClose
                  ? 'bg-amber-500 hover:bg-amber-600'
                  : 'bg-red-500 hover:bg-red-600'}`}
                style={{ minHeight: '48px' }}
                data-testid="confirm-close-day-btn"
              >
                {processing
                  ? 'Cerrando...'
                  : forceClose
                    ? 'Confirmar Cierre Forzado'
                    : 'Confirmar Cierre'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialogo Historial */}
      <Dialog open={historyDialog} onOpenChange={setHistoryDialog}>
        <DialogContent className="bg-slate-900/95 backdrop-blur-xl border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-oswald text-white flex items-center gap-2">
              <History className="text-cyan-400" size={20} />
              Historial de Jornadas
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-2 pt-4">
            {history.length === 0 ? (
              <p className="text-white/50 text-center py-8">No hay jornadas registradas</p>
            ) : (
              history.map((day) => (
                <div 
                  key={day.id}
                  className={`p-4 rounded-xl border transition-all ${
                    day.status === 'open' 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : 'bg-white/5 border-white/10'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {day.status === 'open' ? (
                        <Sun size={20} className="text-green-400" />
                      ) : (
                        <Moon size={20} className="text-white/40" />
                      )}
                      <div>
                        <p className="font-oswald font-bold text-white">{day.business_date}</p>
                        <p className="text-white/50 text-xs">{day.ref}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={day.status === 'open' ? 'bg-green-500/20 text-green-300' : 'bg-white/10 text-white/50'}>
                        {day.status === 'open' ? 'Abierta' : 'Cerrada'}
                      </Badge>
                      {day.total_sales > 0 && (
                        <p className="font-oswald font-bold text-green-400 mt-1">
                          {formatMoney(day.total_sales)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-white/10 text-xs">
                    <div>
                      <span className="text-white/40">Abierta:</span>
                      <p className="text-white/70">{formatTime(day.opened_at)}</p>
                    </div>
                    <div>
                      <span className="text-white/40">Por:</span>
                      <p className="text-white/70">{day.opened_by_name}</p>
                    </div>
                    <div>
                      <span className="text-white/40">Cerrada:</span>
                      <p className="text-white/70">{day.closed_at ? formatTime(day.closed_at) : '-'}</p>
                    </div>
                    <div>
                      <span className="text-white/40">Facturas:</span>
                      <p className="text-white/70">{day.total_invoices || 0}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Reporte Z Dialog — se abre manualmente (legacy) o tras "Cerrar Día"
          (CIERRE-DIA-AUTO-PRINT-2026-05-22). Cuando el modal se cierra y hay
          un cierre pendiente, ejecutamos logout. */}
      <ReportXZ
        type="Z"
        dayId={reportZDialog.dayId}
        open={reportZDialog.open}
        onClose={() => {
          setReportZDialog({ open: false, dayId: null });
          if (closeDayLogoutPending) {
            setCloseDayLogoutPending(false);
            setTimeout(() => {
              logout();
              window.location.href = '/login';
            }, 600);
          }
        }}
      />
    </div>
  );
}
